import alertService from '../services/alert.service'
import api, {
  PaginateWithTypeRequest,
  PaginateResponse,
  ResponseCallback
} from '../services/api.service'
import routeUtil from '../utils/route.util'
import { Markup } from '../views/LabelBuilder/LabelBuilder.slice'
import { CustomLabelDataData } from './customLabelData.api'
import { CustomLabelPrintData } from './customLabelPrint.api'
import { ItemData } from './item.api'
import { UserData } from './user.api'

export interface CustomLabelData {
  id: number
  siteId: number
  createdBy: number
  updatedBy: number
  deletedBy?: number
  name: string
  csvFilename?: string
  apiUrl?: string
  deletedAt?: string
  createdAt: string
  updatedAt: string
  remainingPrintablesCount: number
  printedCount: number
  hasPrintableLimit: boolean
  attributes: Record<string, any>
  updatedUser?: UserData
  item?: ItemData
  datables: CustomLabelDataData['datable'][]
  printed: Record<
    Exclude<CustomLabelDataData['datable'], 'item'>,
    { total: number, printed: number }
  >
}

export interface CustomLabelCreateRequest {
  name?: string
  item?: number
  csv?: File | null
  csvFilename?: string
  apiUrl?: string
}

type CustomLabelPaginateResponse = PaginateResponse<CustomLabelData[]>
type CustomLabelPaginatePrintsResponse = PaginateResponse<CustomLabelPrintData[]>

export interface CustomLabelUpdateRequest {
  name?: string
  item?: number
  csv?: File | null
  csvFilename?: string
  apiUrl?: string
}

export interface CustomLabelPrintRequest {
  amount?: number
}

export interface CustomLabelReprintPrintsRequest {
  prints?: number[]
}

export interface CustomLabelDeletePrintsRequest {
  prints?: number[]
}

interface CustomLabelDeletePrintsResponse {
  deleted: boolean
}

const routes = {
  create: '/systemLabels',
  read: '/systemLabels/{id}',
  paginate: '/systemLabels/paginate',
  search: '/systemLabels/search',
  paginatePrints: '/systemLabels/{id}/prints/paginate',
  update: '/systemLabels/{id}',
  preview: '/systemLabels/{id}/preview',
  print: '/systemLabels/{id}/print',
  delete: '/systemLabels/{id}',
  reprintPrints: '/systemLabels/{id}/prints/reprint',
  deletePrints: '/systemLabels/{id}/prints/delete'
}

export default {
  create (
    payload: CustomLabelCreateRequest,
    callback: ResponseCallback<CustomLabelData>
  ) {
    api.postForm(routes.create, payload, callback)
  },

  read (id: number, callback: ResponseCallback<CustomLabelData>) {
    const url = routeUtil.replaceParams(routes.read, { id })
    api.get(url, {}, callback)
  },

  paginate (
    params: PaginateWithTypeRequest,
    callback: ResponseCallback<CustomLabelPaginateResponse>
  ) {
    api.get(routes.paginate, params, callback)
  },

  search (
    term: string,
    callback: ResponseCallback<CustomLabelData[]>
  ) {
    const params = { term }
    api.get(routes.search, params, callback)
  },

  paginatePrints (
    id: number,
    params: PaginateWithTypeRequest,
    callback: ResponseCallback<CustomLabelPaginatePrintsResponse>) {
    const url = routeUtil.replaceParams(routes.paginatePrints, { id })
    api.get(url, params, callback)
  },

  update (
    id: number,
    payload: CustomLabelUpdateRequest,
    callback: ResponseCallback<CustomLabelData>
  ) {
    const url = routeUtil.replaceParams(routes.update, { id })
    api.postForm(url, payload, callback)
  },

  preview (id: number, callback: ResponseCallback<Markup>) {
    const url = routeUtil.replaceParams(routes.preview, { id })
    alertService.info('Custom label preview generating...')
    api.get(url, {}, callback)
  },

  print (
    id: number,
    payload: CustomLabelPrintRequest,
    callback: ResponseCallback<Markup[]>
  ) {
    const url = routeUtil.replaceParams(routes.print, { id })
    alertService.info('Custom labels generating...')
    api.post(url, payload, callback)
  },

  reprintPrints (
    id: number,
    payload: CustomLabelReprintPrintsRequest,
    callback: ResponseCallback<Markup[]>
  ) {
    const url = routeUtil.replaceParams(routes.reprintPrints, { id })
    alertService.info('Custom labels generating...')
    api.post(url, payload, callback)
  },

  delete (id: number, callback: ResponseCallback<CustomLabelData>) {
    const url = routeUtil.replaceParams(routes.delete, { id })
    api.delete(url, {}, callback)
  },

  deletePrints (
    id: number,
    payload: CustomLabelDeletePrintsRequest,
    callback: ResponseCallback<CustomLabelDeletePrintsResponse>
  ) {
    const url = routeUtil.replaceParams(routes.deletePrints, { id })
    api.post(url, payload, callback)
  }
}
